import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { LazyImage } from '@/components/ui/LazyImage';
import { useInitialData } from '@/contexts/InitialDataContext';

interface Stat {
  label: string;
  value: string;
}

interface TrustLogo {
  id: string;
  name: string;
  imageUrl: string;
}

interface TrustSectionData {
  heading: string;
  stats: Stat[];
  logos: TrustLogo[];
}

const LOCAL_LOGO_MAP: Record<string, string> = {
  'afrocentric group': '/images/clients/afrocentric-group.webp',
  'bidvest': '/images/clients/bidvest.webp',
  'bmw': '/images/clients/bmw.svg',
  'des moines police department': '/images/clients/des-moines-police-department.webp',
  'ford': '/images/clients/ford.svg',
  'medscheme': '/images/clients/medscheme.webp',
  'miniature market': '/images/clients/miniature-market.webp',
  'mr price': '/images/clients/mr-price.webp',
  'nedbank': '/images/clients/nedbank.webp',
  'old mutual': '/images/clients/old-mutual.webp',
  'pep': '/images/clients/pep.svg',
  'standard bank': '/images/clients/standard-bank.webp',
  'toyota': '/images/clients/toyota.webp',
  'volksvagen': '/images/clients/volkswagen.svg',
  'volkswagen': '/images/clients/volkswagen.svg'
};

const FALLBACK_LOGOS: TrustLogo[] = [
  { id: '1', name: 'AfroCentric Group', imageUrl: '/images/clients/afrocentric-group.webp' },
  { id: '2', name: 'Bidvest', imageUrl: '/images/clients/bidvest.webp' },
  { id: '3', name: 'BMW', imageUrl: '/images/clients/bmw.svg' },
  { id: '4', name: 'Des Moines Police Department', imageUrl: '/images/clients/des-moines-police-department.webp' },
  { id: '5', name: 'Ford', imageUrl: '/images/clients/ford.svg' },
  { id: '6', name: 'Medscheme', imageUrl: '/images/clients/medscheme.webp' },
  { id: '7', name: 'Miniature Market', imageUrl: '/images/clients/miniature-market.webp' },
  { id: '8', name: 'Mr Price', imageUrl: '/images/clients/mr-price.webp' },
  { id: '9', name: 'Nedbank', imageUrl: '/images/clients/nedbank.webp' },
  { id: '10', name: 'Old Mutual', imageUrl: '/images/clients/old-mutual.webp' },
  { id: '11', name: 'PEP', imageUrl: '/images/clients/pep.svg' },
  { id: '12', name: 'Standard Bank', imageUrl: '/images/clients/standard-bank.webp' },
  { id: '13', name: 'Toyota', imageUrl: '/images/clients/toyota.webp' },
  { id: '14', name: 'Volkswagen', imageUrl: '/images/clients/volkswagen.svg' }
];

const FALLBACK_DATA: TrustSectionData = {
  heading: 'TRUSTED BY PRACTITIONERS AND ORGANISATIONS',
  stats: [
    { label: 'CERTIFIED PRACTITIONERS', value: 'Globally' },
    { label: '98% SUCCESS RATE', value: 'In Team Efficiency' }
  ],
  logos: FALLBACK_LOGOS
};

function getClientLogoAlt(name: string): string {
  const normalized = name.trim();
  if (/mr\.?\s*price/i.test(normalized)) {
    return 'Mr Price Group company logo — FIVE LENS Enneagram client';
  }
  if (/^pep$/i.test(normalized) || /pep\s*stores/i.test(normalized)) {
    return 'PEP Stores company logo — FIVE LENS Enneagram client';
  }
  return `${normalized} company logo — FIVE LENS Enneagram client`;
}

export function TrustLogos() {
  const initialData = useInitialData();
  const initialTrust = initialData?.trustSection;

  const [data, setData] = useState<TrustSectionData>(() => {
    if (initialTrust && typeof initialTrust === 'object') {
      return {
        heading: initialTrust.heading || FALLBACK_DATA.heading,
        stats: Array.isArray(initialTrust.stats) && initialTrust.stats.length > 0 ? initialTrust.stats : FALLBACK_DATA.stats,
        logos: Array.isArray(initialTrust.logos) && initialTrust.logos.length > 0 ? initialTrust.logos : FALLBACK_DATA.logos,
      };
    }
    return FALLBACK_DATA;
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If initial data was provided by SSR cache, skip client Firestore read
    if (initialTrust?.logos && initialTrust.logos.length > 0) {
      return;
    }

    const fetchData = async () => {
      try {
        const res = await fetch('/api/public/trust-logos');
        if (res.ok) {
          const fetchedData = await res.json() as TrustSectionData;
          if (fetchedData) {
            const mergedStats = Array.isArray(fetchedData.stats) && fetchedData.stats.length > 0 ? fetchedData.stats : FALLBACK_DATA.stats;
            if (fetchedData.logos && fetchedData.logos.length > 0) {
              const normalizedLogos = fetchedData.logos.map(logo => {
                const nameLower = (logo.name || '').toLowerCase().trim();
                const fixedName = nameLower === 'volksvagen' ? 'Volkswagen' : logo.name;
                let imgUrl = logo.imageUrl || '';
                if (
                  !imgUrl.startsWith('/') &&
                  !imgUrl.includes('/media/trust-logos/') &&
                  LOCAL_LOGO_MAP[nameLower]
                ) {
                  imgUrl = LOCAL_LOGO_MAP[nameLower];
                }
                return {
                  ...logo,
                  name: fixedName,
                  imageUrl: imgUrl
                };
              });
              setData({ heading: fetchedData.heading || FALLBACK_DATA.heading, stats: mergedStats, logos: normalizedLogos });
            } else {
              setData({ heading: fetchedData.heading || FALLBACK_DATA.heading, stats: mergedStats, logos: FALLBACK_DATA.logos });
            }
          }
        }
      } catch (error) {
        console.warn('Error fetching trust logos:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [initialTrust]);

  const safeStats = Array.isArray(data?.stats) && data.stats.length > 0 ? data.stats : FALLBACK_DATA.stats;
  const safeLogos = Array.isArray(data?.logos) && data.logos.length > 0 ? data.logos : FALLBACK_DATA.logos;
  const hasLogos = safeLogos.length > 0;
  
  // Need multiple duplicates if the list is small so the marquee doesn't have gaps
  const minimumItems = 12;
  let logosToRender = [...safeLogos];
  
  if (hasLogos && logosToRender.length < minimumItems) {
    const multiplier = Math.ceil(minimumItems / logosToRender.length);
    const newLogos = [];
    for (let i = 0; i < multiplier; i++) {
      newLogos.push(...logosToRender);
    }
    logosToRender = newLogos;
  }
  
  // Double it one more time to be safe for the sliding animation
  if (hasLogos) {
    logosToRender = [...logosToRender, ...logosToRender];
  }

  if (loading) {
    return (
      <section className="py-12 bg-ground-alt text-ink border-b border-ground-deep min-h-[140px] flex items-center justify-center">
        <div className="animate-spin w-6 h-6 border-2 border-rule-3 border-t-transparent rounded-full" />
      </section>
    );
  }

  return (
    <section className="py-12 bg-ground-alt text-ink border-b border-ground-deep overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-8 flex flex-col md:flex-row justify-between items-center gap-8">
        <div>
          <span className="text-[10px] uppercase tracking-[0.2em] text-ink/70 font-semibold">{data?.heading || FALLBACK_DATA.heading}</span>
        </div>
        <div className="flex gap-8 md:gap-12 w-full md:w-auto justify-between md:justify-end">
          {safeStats.map((stat, idx) => (
            <div key={idx} className="text-right">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-ink/70 mb-1 font-semibold">{stat.label}</span>
              <span className="text-xl font-light italic font-serif text-ink">{stat.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Infinite Carousel - Light Surface Mode */}
      {hasLogos && (
        <div className="relative w-full flex items-center h-18 md:h-22 mt-4">
          {/* Gradient edge fades */}
          <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-r from-ground-alt to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-l from-ground-alt to-transparent z-10 pointer-events-none"></div>
          
          <motion.div
            className="flex w-max items-center"
            style={{ willChange: 'transform', transform: 'translateZ(0)' }}
            animate={{ x: ['0%', '-50%'] }}
            transition={{ repeat: Infinity, ease: 'linear', duration: 55 }}
          >
            {logosToRender.map((logo, index) => (
              <div 
                key={`${logo.id}-${index}`} 
                className="flex items-center justify-center gap-4 px-6 md:px-10 text-ink/70 hover:text-ink transition-colors duration-300 min-w-[170px] md:min-w-[220px]"
              >
                {logo.imageUrl ? (
                  <LazyImage
                    src={logo.imageUrl}
                    alt={getClientLogoAlt(logo.name)}
                    className="h-10 md:h-12 max-w-[140px] md:max-w-[170px] object-contain opacity-70 hover:opacity-100 transition-all duration-300 filter grayscale hover:grayscale-0"
                    width={170}
                    height={48}
                    style={{
                      imageRendering: '-webkit-optimize-contrast',
                      transform: 'translateZ(0)',
                      backfaceVisibility: 'hidden',
                      mixBlendMode: 'multiply',
                    }}
                  />
                ) : (
                  <span className="font-bold text-base md:text-lg tracking-wider uppercase text-ink/70 hover:text-ink transition-opacity">{logo.name}</span>
                )}
              </div>
            ))}
          </motion.div>
        </div>
      )}
    </section>
  );
}
